import { QueryClient } from '@tanstack/react-query';

/*
 * TanStack Query, configured once. This is the structural fix for P7.
 *
 * The prototype fires `GET /api/notifications?limit=30` **three to four times
 * per page load**, and again on every navigation, with no deduplication and no
 * cache. That is not a bug in any one component — it is four components each
 * calling fetch in an effect, which is what happens when there is no query
 * layer at all. A cache with request deduplication makes the same four calls
 * one call, without any component knowing about the others.
 *
 * The defaults below are chosen for this application specifically. An
 * operations tool is left open on a desk all day, so the interesting question
 * is not "is this fast" but "is what I am looking at still true".
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /*
         * Thirty seconds. Long enough that the four components sharing a query
         * on one screen make one request between them; short enough that a job
         * status changed by someone else does not sit stale on a manager's
         * screen for minutes.
         */
        staleTime: 30_000,

        /*
         * Refetch when the tab is focused again. This is an operations tool
         * that stays open — coming back to the window is exactly the moment
         * someone wants to know what changed while they were out.
         */
        refetchOnWindowFocus: true,

        // Not on every remount. That is what turns navigating back to a list
        // into a request, which is half of what P7 actually is.
        refetchOnMount: false,

        /*
         * Do not retry a request the server refused. A 401, 403, 404 or 422 is
         * an answer, not a failure — retrying it three times delays the error
         * the user needs to see and, for a 401, sends two more requests with
         * credentials that have already been rejected.
         */
        retry: (failureCount, error) => {
          if (failureCount >= 2) return false;
          const status = (error as { status?: number }).status;
          if (typeof status === 'number' && status >= 400 && status < 500) return false;
          return true;
        },
      },
      mutations: {
        // Never automatically. A mutation that runs twice is a job created
        // twice — idempotency is designed per operation (CLAUDE.md §4), not
        // assumed by a retry policy.
        retry: false,
      },
    },
  });
}
